import mila from 'markdown-it-link-attributes';
import mentionPlugin from './markdownIt/link';
import MarkdownIt from 'markdown-it';

const setImageHeight = inlineToken => {
  const imgSrc = inlineToken.attrGet('src');
  if (!imgSrc) return;
  const url = new URL(imgSrc);
  const height = url.searchParams.get('cw_image_height');
  if (!height) return;
  inlineToken.attrSet('style', `height: ${height};`);
};

const processInlineToken = blockToken => {
  blockToken.children.forEach(inlineToken => {
    if (inlineToken.type === 'image') {
      setImageHeight(inlineToken);
    }
  });
};

const imgResizeManager = md => {
  // Custom rule for image resize in markdown
  // If the image url has a query param cw_image_height, then add a style attribute to the image
  md.core.ruler.after('inline', 'add-image-height', state => {
    state.tokens.forEach(blockToken => {
      if (blockToken.type === 'inline') {
        processInlineToken(blockToken);
      }
    });
  });
};

// Aceleres 2026-08-12: regras de bloco do Markdown que o WhatsApp nao tem.
//
// Uma mensagem de chat nao e um documento: quem escreve "-----" quer ver cinco
// tracos, nao uma linha horizontal — e como o `hr` nao deixa texto nenhum, a
// mensagem sumia por completo (aconteceu de verdade, mensagem 219786). O mesmo
// com "- item", "# titulo", "1. primeiro" e "> citacao": todos comiam o
// primeiro caractere. Nenhum destes existe no WhatsApp, entao o contato do
// outro lado ve o caractere e no painel ele desaparecia.
//
// `link` sai junto porque "[texto](url)" tambem some no WhatsApp; URLs
// continuam clicaveis pelo `linkify`, que e o que o WhatsApp faz.
const BLOCK_RULES_WITHOUT_WHATSAPP_EQUIVALENT = [
  'hr',
  'heading',
  'lheading',
  'list',
  'blockquote',
  'code',
  'fence',
  'reference',
  'html_block',
];

// `backticks` entra aqui porque a crase simples nao e sintaxe do WhatsApp: no
// telemovel ela aparece como crase, e no painel desaparecia.
const INLINE_RULES_WITHOUT_WHATSAPP_EQUIVALENT = [
  'link',
  'html_inline',
  'backticks',
];

// O WhatsApp risca com UM til (`~assim~`); o Markdown pede dois. Em vez de uma
// regra nova, duplicamos o til antes de renderizar e deixamos a regra
// `strikethrough`, que ja existe, fazer o trabalho.
//
// O padrao e propositadamente estreito — sem espaco colado por dentro e sem til
// no meio — para nao apanhar um til solto de uma conta ("a ~ b") nem um caminho
// de ficheiro. Quando nao casa, o til fica visivel, que era o pedido.
const WHATSAPP_STRIKETHROUGH =
  /(^|[\s(])~([^\s~][^~]*[^\s~]|[^\s~])~(?=$|[\s.,;:!?)])/g;

const expandWhatsAppStrikethrough = text =>
  text.replace(WHATSAPP_STRIKETHROUGH, '$1~~$2~~');

// O que SOBREVIVE: `*negrito*` e `_italico_`, que sao exatamente os do
// WhatsApp e continuam a formatar igual dos dois lados.
// No Markdown um asterisco e enfase (italico) e dois sao forte (negrito). No
// WhatsApp UM asterisco ja e negrito, e o italico e o underscore. Como o texto
// vem do WhatsApp e volta para la, seguimos a regra de la: `*assim*` sai em
// negrito, `_assim_` em italico. O token guarda o marcador usado (`markup`),
// entao da para separar os dois sem tocar no parser.
const renderEmphasisTheWhatsAppWay = md => {
  const emphasisTag = token => (token.markup === '*' ? 'strong' : 'em');

  md.renderer.rules.em_open = (tokens, idx) => `<${emphasisTag(tokens[idx])}>`;
  md.renderer.rules.em_close = (tokens, idx) =>
    `</${emphasisTag(tokens[idx])}>`;
};

// No WhatsApp nao existe paragrafo: existe linha. A linha em branco do texto
// ("\n\n") e o que separa dois blocos, e e assim que ela tem de aparecer na
// bolha.
//
// O markdown-it monta um <p> por bloco, mas a bolha desenha TODO <p> como
// `inline`, sem margem (`[&_p]:!inline [&_p]:!m-0` em Text/Index.vue e nas
// legendas de imagem/video) — isso existe para o horario poder correr atras da
// ultima linha. O efeito colateral e que dois paragrafos apareciam colados na
// MESMA linha: a linha em branco sumia e a propria quebra junto com ela.
//
// A quebra e reposta aqui, no ponto exato onde ela estava no texto, e nao
// mexendo no <p>: a escala de 15,8px da bolha depende de `.prose-bubble p`
// existir (aceleres-overrides.css), entao tirar o paragrafo encolheria o texto
// de todas as mensagens.
const renderParagraphBreakAsBlankLine = md => {
  const original = md.renderer.rules.paragraph_open;

  md.renderer.rules.paragraph_open = (tokens, idx, options, env, self) => {
    const padrao = original
      ? original(tokens, idx, options, env, self)
      : self.renderToken(tokens, idx, options);

    const seguePresenteOutroParagrafo =
      idx > 0 && tokens[idx - 1].type === 'paragraph_close';

    return seguePresenteOutroParagrafo ? `<br /><br />${padrao}` : padrao;
  };
};

const restrictToWhatsAppSyntax = md => {
  md.disable(BLOCK_RULES_WITHOUT_WHATSAPP_EQUIVALENT, true);
  md.disable(INLINE_RULES_WITHOUT_WHATSAPP_EQUIVALENT, true);
  renderEmphasisTheWhatsAppWay(md);
  renderParagraphBreakAsBlankLine(md);
  return md;
};

const createMarkdownInstance = (linkify = true) => {
  return MarkdownIt({
    html: false,
    xhtmlOut: true,
    breaks: true,
    langPrefix: 'language-',
    linkify,
    // Preserve the exact punctuation typed by the user. markdown-it's
    // typographer normalizes repeated `?` and `!` characters to three, which
    // makes messages look truncated even though the stored content is intact.
    typographer: false,
    quotes: '\u201c\u201d\u2018\u2019',
    maxNesting: 20,
  })
    .use(mentionPlugin)
    .use(imgResizeManager)
    .use(mila, {
      attrs: {
        class: 'link',
        rel: 'noreferrer noopener nofollow',
        target: '_blank',
      },
    });
};

const messageLinkifier = createMarkdownInstance().linkify;

export const firstWebLink = text => {
  const links = messageLinkifier.match(text || '');
  return links?.find(link => /^https?:\/\//i.test(link.url))?.url || '';
};

const TWITTER_USERNAME_REGEX = /(^|[^@\w])@(\w{1,15})\b/g;
const TWITTER_USERNAME_REPLACEMENT = '$1[@$2](http://twitter.com/$2)';
const TWITTER_HASH_REGEX = /(^|\s)#(\w+)/g;
const TWITTER_HASH_REPLACEMENT = '$1[#$2](https://twitter.com/hashtag/$2)';

class MessageFormatter {
  /**
   * `whatsAppSyntaxOnly` deixa de fora as regras de bloco do Markdown que o
   * WhatsApp nao tem (ver restrictToWhatsAppSyntax). Fica desligado por
   * omissao de proposito: o mesmo formatador desenha respostas do Copilot,
   * artigos e notas, onde lista e titulo sao legitimos. So a bolha da conversa
   * pede o modo restrito.
   */
  constructor(
    message,
    isATweet = false,
    isAPrivateNote = false,
    linkify = true,
    whatsAppSyntaxOnly = false
  ) {
    this.message = message || '';
    this.isAPrivateNote = isAPrivateNote;
    this.isATweet = isATweet;
    this.linkify = linkify;
    this.md = createMarkdownInstance(linkify);
    this.whatsAppSyntaxOnly = whatsAppSyntaxOnly;
    if (whatsAppSyntaxOnly) restrictToWhatsAppSyntax(this.md);
  }

  formatMessage() {
    let updatedMessage = this.message;
    if (this.isATweet && !this.isAPrivateNote) {
      updatedMessage = updatedMessage.replace(
        TWITTER_USERNAME_REGEX,
        TWITTER_USERNAME_REPLACEMENT
      );
      updatedMessage = updatedMessage.replace(
        TWITTER_HASH_REGEX,
        TWITTER_HASH_REPLACEMENT
      );
    }
    if (this.whatsAppSyntaxOnly) {
      updatedMessage = expandWhatsAppStrikethrough(updatedMessage);
    }

    return this.md.render(updatedMessage);
  }

  get formattedMessage() {
    return this.formatMessage();
  }

  get plainText() {
    const strippedOutHtml = new DOMParser().parseFromString(
      this.formattedMessage,
      'text/html'
    );
    return strippedOutHtml.body.textContent || '';
  }
}

export default MessageFormatter;
